/**
 * Algolia-powered PredictiveSearch Web Component
 *
 * Overrides the new theme's PredictiveSearch so Algolia handles products
 * while Shopify's Section Rendering API handles everything else
 * (query suggestions, collections, articles, pages).
 *
 * THIS FILE MUST BE LOADED BEFORE theme.js — theme.js checks
 * `if (!window.customElements.get("predictive-search"))` before registering
 * its own version, so loading ours first wins.
 *
 * Dependencies (loaded by algolia-config.liquid, also before theme.js):
 *   - algoliasearch@4 (CDN, defer)
 *   - search-insights@2 (CDN, defer)
 *   - window.algoliaConfig  { appId, apiKey, indexName }
 *   - window.moneyFormat    (Shopify money format string)
 */

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    // The search form points to this element via aria-owns="[this.id]"
    this._searchForm = document.querySelector(`[aria-owns="${this.id}"]`);
    if (!this._searchForm) return;

    this._queryInput = this._searchForm.elements['q'];
    if (!this._queryInput) return;

    // Attach shadow DOM — mirrors how the theme's component works so
    // the drawer CSS (which uses slots) renders correctly
    if (!this.shadowRoot) {
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = '<slot name="placeholder"></slot>';
    }

    this._abortController = null;
    this._currentQueryID = null;
    this._algoliaIndex = null; // lazy init on first search

    this._queryInput.addEventListener(
      'input',
      this._debounce(() => this._onInputChanged(), 300)
    );

    this._searchForm.addEventListener('reset', () => this._onSearchCleared());
    this._searchForm.addEventListener('submit', (e) => {
      if (!this._queryInput.value.trim()) e.preventDefault();
    });
  }

  disconnectedCallback() {
    this._abortController?.abort();
  }

  // ─── Algolia ────────────────────────────────────────────────────────────────

  _initAlgolia() {
    if (this._algoliaIndex) return true;

    const cfg = window.algoliaConfig;
    if (!cfg?.appId || !cfg?.apiKey) {
      console.warn('[PredictiveSearch] Algolia credentials not set in window.algoliaConfig');
      return false;
    }
    if (typeof algoliasearch === 'undefined') {
      console.warn('[PredictiveSearch] algoliasearch library not loaded yet');
      return false;
    }

    const client = algoliasearch(cfg.appId, cfg.apiKey);
    this._algoliaIndex = client.initIndex(cfg.indexName || 'shopify_products');

    // Init Insights if not already done
    if (typeof aa === 'function') {
      aa('init', { appId: cfg.appId, apiKey: cfg.apiKey });
    }

    return true;
  }

  async _searchAlgolia(query) {
    if (!this._initAlgolia()) return [];

    try {
      const { hits, queryID } = await this._algoliaIndex.search(query, {
        hitsPerPage: 10,
        clickAnalytics: true, // required for queryID (conversion tracking)
        filters:
          'NOT product_type:Sample AND NOT product_type:"Gift Card" AND NOT product_type:Reward AND NOT tags:hide AND NOT tags:bogos-gift',
        attributesToRetrieve: [
          'objectID',
          'handle',
          'title',
          'product_type',
          'tags',
          '_tags',
          'variants',
          'images',
          'product_image',
          'available',
          'inventory_available',
          'inventory_policy',
          'variants_min_price',
          'meta',
        ],
      });

      this._currentQueryID = queryID;
      return this._deduplicateProducts(hits);
    } catch (e) {
      console.error('[PredictiveSearch] Algolia error:', e);
      return [];
    }
  }

  // ─── Shopify Section API (non-product tabs) ──────────────────────────────

  async _searchShopify(query, signal) {
    const root = window.Shopify?.routes?.root || '/';
    const params = new URLSearchParams({
      q: query,
      section_id: 'predictive-search',
      'resources[limit]': '10',
      'resources[limit_scope]': 'each',
    });
    const res = await fetch(`${root}search/suggest?${params}`, { signal });
    return res.text();
  }

  // ─── Input handlers ──────────────────────────────────────────────────────

  _onInputChanged() {
    const query = this._queryInput.value.trim();
    if (!query) return this._onSearchCleared();

    this._abortController?.abort();
    this._abortController = new AbortController();
    this._doSearch(query);
  }

  _onSearchCleared() {
    this._abortController?.abort();
    this._queryInput.focus();
    this.shadowRoot.innerHTML = '<slot name="placeholder"></slot>';
  }

  // ─── Main search orchestrator ────────────────────────────────────────────

  async _doSearch(query) {
    document.documentElement.dispatchEvent(
      new CustomEvent('theme:loading:start', { bubbles: true })
    );

    try {
      // Algolia (products) + Shopify (non-products) run in parallel
      const [algoliaProducts, shopifyHTML] = await Promise.all([
        this._searchAlgolia(query),
        this._searchShopify(query, this._abortController.signal),
      ]);

      // Parse the server-rendered section
      const tempDoc = new DOMParser().parseFromString(shopifyHTML, 'text/html');
      const section = tempDoc.querySelector('.shopify-section');
      if (!section) return;

      // Inject Algolia products into the dedicated container
      const productsContainer = section.querySelector('.predictive-search__products');

      if (productsContainer) {
        if (algoliaProducts.length > 0) {
          productsContainer.innerHTML = algoliaProducts
            .map((p) => this._renderProductCard(p))
            .join('');
        } else {
          // Algolia found nothing — remove the products tab + panel entirely.
          // Products is always the first [slot="tab"] / [slot="tabpanel"] pair.
          const panel = productsContainer.closest('[slot="tabpanel"]');
          const xTabs = panel?.parentElement;
          const btn = xTabs?.querySelector(':scope > [slot="tab"]');
          panel?.remove();
          btn?.remove();
        }
      }

      // Put the final HTML into the light-DOM slot="results" div
      const resultsSlot = this.querySelector('[slot="results"]');
      if (resultsSlot) {
        resultsSlot.replaceChildren(
          ...Array.from(document.importNode(section, true).children)
        );
      }

      // Switch shadow DOM to show results slot
      this.shadowRoot.innerHTML = '<slot name="results"></slot>';

      // Attach queryID to any product-form elements for conversion tracking
      if (this._currentQueryID) {
        this._attachQueryIDToForms(this._currentQueryID);
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        console.error('[PredictiveSearch] Search error:', e);
      }
    } finally {
      document.documentElement.dispatchEvent(
        new CustomEvent('theme:loading:end', { bubbles: true })
      );
    }
  }

  // ─── Conversion tracking ─────────────────────────────────────────────────

  _attachQueryIDToForms(queryID) {
    this.querySelectorAll('product-form').forEach((form) => {
      form.algoliaQueryID = queryID;
      form.setAttribute('data-algolia-query-id', queryID);
    });
  }

  // ─── Product card HTML ───────────────────────────────────────────────────

  _deduplicateProducts(hits) {
    const seen = new Map();
    hits.forEach((hit) => {
      const id = hit.handle || (hit.objectID || '').split('-')[0];
      if (id && !seen.has(id)) seen.set(id, hit);
    });
    return Array.from(seen.values());
  }

  /**
   * Generates an HTML string that matches the new theme's product-card
   * structure so existing CSS rules apply without extra work.
   */
  _renderProductCard(product) {
    const productUrl = `/products/${this._escape(product.handle)}`;
    const imgHTML = product.product_image
      ? this._generateImage(product.product_image, product.title)
      : '';
    const priceHTML = this._generatePrice(product);

    return `
      <product-card class="product-card product-card--info-outside" handle="${this._escape(product.handle)}">
        <div class="product-card__figure">
          <a href="${productUrl}" class="product-card__media">
            ${imgHTML}
          </a>
        </div>
        <div class="product-card__info">
          <div class="v-stack gap-2.5 justify-items-start text-start">
            <div class="v-stack gap-0.5">
              <div class="v-stack gap-2.5">
                <div class="v-stack gap-1">
                  <div class="v-stack gap-0.5">
                    <a href="${productUrl}" class="product-title text-sm">
                      ${this._escape(product.title)}
                    </a>
                  </div>
                </div>
              </div>
              <div class="v-stack gap-1">
                ${priceHTML}
              </div>
            </div>
          </div>
        </div>
      </product-card>
    `;
  }

  _generateImage(imageUrl, title = '') {
    const widths = [200, 300, 400, 500, 600, 700, 800, 900, 1000, 1200, 1400, 1600];
    const srcset = widths
      .map((w) => `${this._shopifyImageUrl(imageUrl, w)} ${w}w`)
      .join(', ');
    const src = this._shopifyImageUrl(imageUrl, 500);

    return `<img
      src="${src}"
      srcset="${srcset}"
      sizes="(max-width: 699px) 50vw, 200px"
      alt="${this._escape(title)}"
      loading="lazy"
      class="product-card__image product-card__image--primary aspect-adapt">`;
  }

  _shopifyImageUrl(url, width) {
    if (!url || !url.includes('cdn.shopify.com')) return url;
    return url
      .replace(
        /_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048|master|\d+x\d*)\./gi,
        '.'
      )
      .replace(/\.(jpg|jpeg|png|gif|webp)/i, `_${width}x.$1`);
  }

  _generatePrice(product) {
    const variant = product.variants?.[0] || {};
    // Algolia stores prices in dollars, Shopify formatMoney expects cents
    const priceValue = variant?.price ?? product?.variants_min_price;
    if (!priceValue) return '';

    const priceInCents = priceValue * 100;
    const compareAtPriceInCents = variant?.compare_at_price
      ? variant.compare_at_price * 100
      : null;

    const isOnSale =
      compareAtPriceInCents && compareAtPriceInCents > priceInCents;

    const price = this._formatMoney(priceInCents);
    const compareAtPrice = compareAtPriceInCents
      ? this._formatMoney(compareAtPriceInCents)
      : null;

    if (isOnSale) {
      return `
        <div class="price-list">
          <sale-price class="text-sm">${price}</sale-price>
          <compare-at-price class="text-sm line-through text-subdued">${compareAtPrice}</compare-at-price>
        </div>`;
    }

    return `
      <div class="price-list">
        <sale-price class="text-sm">${price}</sale-price>
      </div>`;
  }

  // ─── Utilities ───────────────────────────────────────────────────────────

  _formatMoney(cents) {
    return window.Shopify?.formatMoney
      ? window.Shopify.formatMoney(cents, window.moneyFormat || '${{amount}}')
      : `$${(cents / 100).toFixed(2)}`;
  }

  _escape(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  _debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

// Guard so this file can be included multiple times without throwing
if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
